import { Workbook, WorksheetView, Worksheet, CellValue, Column, Row, Cell } from 'exceljs';
import pick from 'lodash/pick';


const styleProperties = ['numFmt', 'font', 'alignment', 'protection', 'border', 'fill'] as const;
const additionalColumnProperties = ['width', 'outlineLevel'] as const;

type RowStyle = Pick<Row, typeof styleProperties[number]>;
type ColumnStyle = Pick<
  Partial<Column>,
  typeof styleProperties[number] | typeof additionalColumnProperties[number]
>
type CellStyle = {
  address: { row: number, column: number },
  style: Pick<Cell, typeof styleProperties[number]>
}
type Metadata = {
  sheetName: string,
  views: Partial<WorksheetView>[],
  rowStyles: RowStyle[],
  columnStyles: ColumnStyle[],
  cellStyles: CellStyle[]
};
type Grid = CellValue[][];
type Sheet = {
  grid: Grid,
  metadata: Metadata,
};


const getValues = (worksheet: Worksheet): CellValue[][] => {
  const rows: CellValue[][] = [];
  worksheet.eachRow({ includeEmpty: true }, row => {
    const values: CellValue[] = [];
    row.eachCell({ includeEmpty: true }, cell => {
      const isMerged = (cell as Cell & { isMerged?: boolean }).isMerged;
      const master = (cell as Cell & { master?: Cell }).master;
      const isNonMasterMerged = Boolean(
        isMerged
        && master
        && master.address !== cell.address
        && master.value === cell.value
      );
      values.push(isNonMasterMerged ? null : cell.value);
    });
    rows.push(values);
  });
  return rows;
};

const getColumnStyles = (worksheet: Worksheet) => {
  const columnStyles: ColumnStyle[] = [];
  worksheet.columns.forEach(column =>
    columnStyles.push(pick(column, [...additionalColumnProperties, ...styleProperties]))
  );
  return columnStyles;
};

const getRowStyles = (worksheet: Worksheet) => {
  const rowStyles: RowStyle[] = [];
  worksheet.eachRow({ includeEmpty: true }, row => rowStyles.push(pick(row, styleProperties)));
  return rowStyles;
};

const getCellStyles = (worksheet: Worksheet) => {
  const cellStyles: CellStyle[] = [];

  worksheet.eachRow((row, rowNumber) => {
    row.eachCell((cell, colNumber) => {
      cellStyles.push({
        address: { row: rowNumber - 1, column: colNumber - 1 }, // excel-generator uses 0-indexed columns / rows
        style: pick(cell, styleProperties),
      });
    });
  });
  return cellStyles;
};

export const formatWorkbook = (workbook: Workbook): Sheet[] => {
  const sheets: Sheet[] = [];
  workbook.eachSheet(worksheet => {
    const sheet: Sheet = {
      grid: getValues(worksheet),
      metadata: {
        sheetName: worksheet.name,
        views: worksheet.views,
        columnStyles: getColumnStyles(worksheet),
        rowStyles: getRowStyles(worksheet),
        cellStyles: getCellStyles(worksheet),
      },
    };
    sheets.push(sheet);
  });

  return sheets;
};
